import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { FormModal } from '@/components/shared/form-modal';
import { Field } from '@/components/shared/field';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { errorMessage } from '@/lib/api';
import { useAddMovement } from '../api';
import { MoneyInput } from '../money-input';

export type MovementKind = 'withdrawal' | 'deposit' | 'adjustment';

const COPY: Record<MovementKind, { title: string; description: string; submit: string; done: string }> = {
  withdrawal: { title: 'Registrar sangria', description: 'Retirada de dinheiro da gaveta. Informe o valor retirado.', submit: 'Registrar sangria', done: 'Sangria registrada.' },
  deposit: { title: 'Registrar suprimento', description: 'Entrada de dinheiro na gaveta, por exemplo troco.', submit: 'Registrar suprimento', done: 'Suprimento registrado.' },
  adjustment: { title: 'Registrar ajuste', description: 'Corrige o dinheiro esperado na gaveta. Escolha se soma ou subtrai.', submit: 'Registrar ajuste', done: 'Ajuste registrado.' },
};

export function MovementModal({ kind, registerId, onClose }: { kind: MovementKind | null; registerId: string; onClose: () => void }) {
  const [amount, setAmount] = useState(0);
  const [reason, setReason] = useState('');
  const [direction, setDirection] = useState<'in' | 'out'>('in');
  const [touched, setTouched] = useState(false);
  const mut = useAddMovement(registerId);

  useEffect(() => {
    if (kind) {
      setAmount(0);
      setReason('');
      setDirection('in');
      setTouched(false);
    }
  }, [kind]);

  if (!kind) return null;
  const copy = COPY[kind];
  const reasonError = touched && reason.trim().length < 3 ? 'Informe o motivo (mínimo de 3 caracteres).' : undefined;
  const amountError = touched && amount <= 0 ? 'Informe um valor maior que zero.' : undefined;

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setTouched(true);
    if (!kind || amount <= 0 || reason.trim().length < 3) return;
    const signedAmount = kind === 'adjustment' && direction === 'out' ? -amount : amount;
    try {
      await mut.mutateAsync({ type: kind, amountCents: signedAmount, reason: reason.trim() });
      toast.success(copy.done);
      onClose();
    } catch (err) {
      toast.error(errorMessage(err));
    }
  }

  return (
    <FormModal
      open
      onOpenChange={(v) => !v && onClose()}
      size="sm"
      title={copy.title}
      description={copy.description}
      footer={
        <>
          <Button type="button" variant="outline" onClick={onClose}>
            Voltar
          </Button>
          <Button type="submit" form="movement-form" disabled={mut.isPending}>
            {mut.isPending ? 'Salvando…' : copy.submit}
          </Button>
        </>
      }
    >
      <form id="movement-form" onSubmit={submit} className="grid gap-4" noValidate>
        {kind === 'adjustment' && (
          <Field id="mov-direction" label="Efeito no caixa">
            <Select value={direction} onValueChange={(v) => setDirection(v as 'in' | 'out')}>
              <SelectTrigger id="mov-direction">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="in">Somar ao caixa</SelectItem>
                <SelectItem value="out">Subtrair do caixa</SelectItem>
              </SelectContent>
            </Select>
          </Field>
        )}
        <Field id="mov-amount" label="Valor" error={amountError}>
          <MoneyInput id="mov-amount" value={amount} onChange={setAmount} aria-invalid={!!amountError} />
        </Field>
        <Field id="mov-reason" label="Motivo" error={reasonError}>
          <Textarea id="mov-reason" value={reason} onChange={(e) => setReason(e.target.value)} maxLength={300} rows={3} aria-invalid={!!reasonError} />
        </Field>
      </form>
    </FormModal>
  );
}
